import { useId } from 'react'
import clsx from 'clsx'
import { BillPreview, ChronPreview, TimelinePreview } from '@/assets/illustration'
import { Tag } from '@/components/tag/Tag'

export type MatterRecordSelectionCardType = 'billing' | 'chronology' | 'timeline'

type SvgComponent = React.ComponentType<React.SVGProps<SVGSVGElement>>

const previewByRecordType: Record<MatterRecordSelectionCardType, SvgComponent> = {
  billing: BillPreview,
  chronology: ChronPreview,
  timeline: TimelinePreview,
}

export interface MatterRecordSelectionCardProps {
  /** Determines which record preview image is shown */
  recordType: MatterRecordSelectionCardType
  /** Record type name shown as the card title, e.g. "Medical Chronology" */
  title: string
  /** Short output label shown as "→ {outputLabel}", e.g. "Chronology" */
  outputLabel: string
  /** Description text below the title */
  description: string
  /** Controlled selection state */
  selected: boolean
  /** Called when the user clicks or presses Space/Enter on the card */
  onSelect: () => void
  /** Optional "beta" badge rendered next to the output label */
  tag?: boolean
  /**
   * Tab index for the card root. Default is 0. Set to -1 on unselected cards when the
   * parent implements roving tabindex so arrow keys move focus within the radiogroup.
   */
  tabIndex?: number
}

/**
 * Selectable radio card for the unified Matter Records selection modal.
 *
 * Renders as `role="radio"` — the parent list must carry `role="radiogroup"` and an
 * accessible label. Arrow-key navigation between cards is the parent's responsibility.
 *
 * Presentational: the caller controls `selected` and handles `onSelect`.
 *
 * Figma: https://www.figma.com/design/dXICWmcPx8xc8DqnJH83No/Medical-Chronology---Billing-Summary?node-id=11735-112188
 */
export function MatterRecordSelectionCard({
  recordType,
  title,
  outputLabel,
  description,
  selected,
  onSelect,
  tag = false,
  tabIndex = 0,
}: MatterRecordSelectionCardProps) {
  const Preview = previewByRecordType[recordType]
  // The header row (title + output label + optional beta) forms the accessible name; the
  // description is exposed separately via aria-describedby. Naming the root with aria-label would
  // replace its contents, hiding the output label, beta tag, and description from assistive tech.
  const labelId = useId()
  const descriptionId = useId()
  return (
    <div
      role="radio"
      aria-checked={selected}
      aria-labelledby={labelId}
      aria-describedby={descriptionId}
      tabIndex={tabIndex}
      onClick={onSelect}
      onMouseDown={(e) => e.preventDefault()}
      onKeyDown={(event) => {
        if (event.key === ' ' || event.key === 'Enter') {
          event.preventDefault()
          onSelect()
        }
      }}
      className={clsx(
        'flex cursor-pointer items-start gap-4 rounded-lg border p-4 transition-colors md:max-h-[104px]',
        'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2',
        selected ? ['bg-surface-brand-selected-subtle border-brand-200'] : ['bg-white border-brand-neutral-200 hover:bg-surface-hover']
      )}
    >
      {/* Left: radio indicator + text */}
      <div className="flex min-w-0 flex-1 self-stretch flex-col gap-2">
        {/* Header row: radio + title + output label */}
        <div id={labelId} className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-2">
          <div className="flex min-w-0 items-center gap-3">
            {/* Radio indicator */}
            <div
              aria-hidden={true}
              className={clsx('size-5 shrink-0 rounded-full bg-white', selected ? 'border-[5px] border-brand-500' : 'border border-brand-neutral-300')}
            />
            <span className="text-base font-medium leading-6 text-brand-neutral-950">{title}</span>
          </div>
          {/* On mobile, indent to align under the title (past the radio + gap). */}
          <div className="flex shrink-0 items-center gap-2 pl-8 sm:pl-0">
            <span aria-hidden={true} className="text-sm font-medium leading-5 text-brand-neutral-700">
              →
            </span>
            <span className="text-sm font-medium leading-5 text-brand-neutral-700">{outputLabel}</span>
            {tag && (
              <Tag variant="filled" color="Black" size="2xs">
                beta
              </Tag>
            )}
          </div>
        </div>
        {/* Description — indented to align under the title. */}
        <p id={descriptionId} className="min-w-0 pl-8 text-sm font-normal leading-5 text-brand-neutral-800">
          {description}
        </p>
      </div>

      {/* Preview illustration is decorative; hidden on mobile where the card is too narrow for it. */}
      <div className="hidden w-[246px] shrink-0 flex-col items-start justify-center max-h-[104px] self-stretch sm:flex">
        <Preview aria-hidden={true} className="size-full" />
      </div>
    </div>
  )
}
